// Copyright (C) 2021 Bystronic Laser AG

import { ComputedRef, ref } from 'vue';
import { useSyncFiltersWithUrlParams } from '@/composables/sync-filters-with-url-params';

/**
 * Implements functionality to handle table filters.
 *
 * - Handles initialization and reset of filter values.
 * - Syncs filters with URL query parameters.
 * - Emits filters-change events
 *
 * Returns:
 * - `filters`. Ref with the current filter values.
 * - `clearAllFilters`. Function to be called when the `filters` must be reset
 *   to their default values as defined in `defaultFilterValues`.
 * - `onFiltersApplied`. Function to be called to apply the filters to the table
 *   when they've changed.
 * - `updateFiltersFromUrlQueryParameters`. Returned only for testing.
 *
 * @param defaultFilterValues Object with the default values for `filters`.
 * @param filterValueSerializer Function called for each filter to serialize it
 *   into a URL query parameter value.
 * @param queryParameterDeserializer Function called when a URL query parameter
 *   has been changed so `filter` can be updated.
 * @param areApplying Computed returning if the filters are being applied.
 *   That's to say `filters` doesn't have the default value (`defaultFilterValues`).
 * @param emit `emit` function defined in the component with a `filters-change`
 *   event to be emitted when the filters change.
 */
export function useTableFilters<T>(
  defaultFilterValues: T,
  filterValueSerializer: (value: any) => string | string[],
  queryParameterDeserializer: (key: keyof T, value: any) => any,
  areApplying: ComputedRef<boolean>,
  emit: (...params: any[]) => void,
) {
  const filters = ref<T>(defaultFilterValues);

  const {
    updateUrlQueryParametersFromFilters,
    updateFiltersFromUrlQueryParameters,
  } = useSyncFiltersWithUrlParams(
    filters,
    defaultFilterValues,
    filterValueSerializer,
    queryParameterDeserializer,
    areApplying,
    emit,
  );

  async function clearAllFilters() {
    initializeFilters();
    await onFiltersApplied();
  }

  function initializeFilters() {
    filters.value = structuredClone(defaultFilterValues);
  }

  async function onFiltersApplied() {
    await updateUrlQueryParametersFromFilters();
    emitFilters();
  }

  function emitFilters() {
    emit('filters-change', filters.value, areApplying.value);
  }

  return {
    filters,
    clearAllFilters,
    onFiltersApplied,
    updateFiltersFromUrlQueryParameters, // exported for testing
  };
}
